import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { message } from 'antd';
import {
  HomeOutlined,
  InboxOutlined,
  PlusOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
  ImportOutlined,
  ExportOutlined,
  UserOutlined,
  TeamOutlined,
  ShopOutlined,
  MedicineBoxOutlined,
  ExclamationCircleOutlined,
  ShoppingCartOutlined,
  AccountBookOutlined
} from '@ant-design/icons';
import StockInModalMobile from '../stock-in/StockInModalMobile';
import StockOutModalMobile from '../stock-out/StockOutModalMobile';
import CustomerModalMobile from '../customers/CustomerModalMobile';
import SupplierModalMobile from '../suppliers/SupplierModalMobile';
import MedicineMobileModal from '../medicines/MedicineMobileModal';
import ExpenseDetailModalMobile from '../expenses/ExpenseDetailModalMobile';
import { medicineService } from '../../services/medicineService';
import { supplierService } from '../../services/supplierService';
import { customerService } from '../../services/customerService';
import { useAuthStore } from '../../stores/authStore';
import { Button, Avatar } from 'antd';
import Logo from './Logo';
import styles from './BottomNavBar.module.css';

const BottomNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();

  // States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKhoPopover, setShowKhoPopover] = useState(false);
  const [showStockInModal, setShowStockInModal] = useState(false);
  const [showStockOutModal, setShowStockOutModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showMedicineModal, setShowMedicineModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showMorePopover, setShowMorePopover] = useState(false);
  const [showReportDrawer, setShowReportDrawer] = useState(false);

  const khoBtnRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLButtonElement>(null);

  // Queries
  const { data: medicines = [] } = useQuery('medicines', () => 
    medicineService.getMedicines()
  );

  const { data: suppliers = [] } = useQuery('suppliers', () => 
    supplierService.getSuppliers()
  );

  // Khóa copy text và context menu
  useEffect(() => {
    // Khóa context menu (chuột phải)
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    // Khóa copy (Ctrl+C, Cmd+C)
    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    // Khóa cut (Ctrl+X, Cmd+X)
    const handleCut = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    // Khóa paste (Ctrl+V, Cmd+V)
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    // Khóa select all (Ctrl+A, Cmd+A)
    const handleSelectAll = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        return false;
      }
    };

    // Khóa drag và drop
    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    // Khóa selection
    const handleSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // Thêm event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('keydown', handleSelectAll);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('selectstart', handleSelectStart);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('keydown', handleSelectAll);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  // Handlers
  const handleStockInSuccess = async () => {
    try {
      await queryClient.invalidateQueries('stock-in');
      await queryClient.invalidateQueries('inventory');
      setShowStockInModal(false);
      message.success('Tạo phiếu nhập kho thành công');
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật dữ liệu');
    }
  };

  const handleStockOutSuccess = async () => {
    try {
      await queryClient.invalidateQueries('stock-out');
      await queryClient.invalidateQueries('inventory');
      setShowStockOutModal(false);
      message.success('Tạo phiếu xuất kho thành công');
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật dữ liệu');
    }
  };

  const handleCustomerSubmit = async (data: any) => {
    try {
      await customerService.createCustomer(data);
      await queryClient.invalidateQueries('customers');
      setShowCustomerModal(false);
      message.success('Tạo khách hàng thành công');
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo khách hàng');
    }
  };

  const handleSupplierSave = async (data: any) => {
    try {
      await supplierService.createSupplier(data);
      await queryClient.invalidateQueries('suppliers');
      setShowSupplierModal(false);
      message.success('Tạo nhà cung cấp thành công');
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo nhà cung cấp');
    }
  };

  const handleMedicineSuccess = async () => {
    try {
      await queryClient.invalidateQueries('medicines');
      setShowMedicineModal(false);
      message.success('Tạo sản phẩm thành công');
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật dữ liệu');
    }
  };

  const handleExpenseSuccess = async () => {
    try {
      await queryClient.invalidateQueries('expenses');
      setShowExpenseModal(false);
      message.success('Tạo chi phí thành công');
      navigate('/expenses');
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo chi phí');
    }
  };

  // Đóng popover khi click ra ngoài
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMorePopover(false);
      }
      if (khoBtnRef.current && !khoBtnRef.current.contains(e.target as Node)) {
        setShowKhoPopover(false);
      }
    };
    if (showMorePopover || showKhoPopover) {
      document.addEventListener('click', handleClick);
    }
    return () => document.removeEventListener('click', handleClick);
  }, [showMorePopover, showKhoPopover]);

  // Xác định active tab dựa vào current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path.includes('/stock-') || path === '/inventory') return 'kho';
    if (path === '/reports' || path.includes('/reports/')) return 'report';
    if (path === '/system' || path === '/customers' || path === '/suppliers' || path === '/medicines' || path === '/debts' || path === '/expenses' || path === '/users') return 'more';
    return '';
  };

  const activeTab = getActiveTab();

  const handleCreateNew = (type: string) => {
    setShowCreateModal(false);
    switch (type) {
      case 'sale':
        navigate('/sale');
        break;
      case 'stock-in':
        setShowStockInModal(true);
        break;
      case 'stock-out':
        setShowStockOutModal(true);
        break;
      case 'expense':
        setShowExpenseModal(true);
        break;
      case 'customer':
        setShowCustomerModal(true);
        break;
      case 'supplier':
        setShowSupplierModal(true);
        break;
      case 'medicine':
        setShowMedicineModal(true);
        break;
    }
  };

  const createMenuItems = [
    { 
      title: 'Bán hàng',
      type: 'sale',
      icon: <ShoppingCartOutlined style={{ fontSize: '20px', color: '#10b981' }} />,
      color: '#ecfdf5',
      borderColor: '#a7f3d0'
    },
    { 
      title: 'Tạo mới Nhập kho',
      type: 'stock-in',
      icon: <ImportOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      color: '#f6ffed',
      borderColor: '#b7eb8f'
    },
    { 
      title: 'Tạo mới Xuất kho',
      type: 'stock-out',
      icon: <ExportOutlined style={{ fontSize: '20px', color: '#1890ff' }} />,
      color: '#e6f7ff',
      borderColor: '#91d5ff'
    },
    { 
      title: 'Tạo mới Chi phí',
      type: 'expense',
      icon: <AccountBookOutlined style={{ fontSize: '20px', color: '#13c2c2' }} />,
      color: '#f0fdfa',
      borderColor: '#99f6e4'
    },
    { 
      title: 'Tạo mới Khách hàng',
      type: 'customer',
      icon: <UserOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      color: '#f9f0ff',
      borderColor: '#d3adf7'
    },
    { 
      title: 'Tạo mới Nhà cung cấp',
      type: 'supplier',
      icon: <ShopOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />,
      color: '#fff7e6',
      borderColor: '#ffd591'
    },
    { 
      title: 'Tạo mới Sản phẩm',
      type: 'medicine',
      icon: <MedicineBoxOutlined style={{ fontSize: '20px', color: '#eb2f96' }} />,
      color: '#fff0f6',
      borderColor: '#ffadd2'
    }
  ];

  const moreMenuItems = [
    {
      title: 'Công nợ khách hàng',
      icon: <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />,
      path: '/debts'
    },
    { 
      title: 'Quản lý chi phí',
      icon: <AccountBookOutlined style={{ fontSize: '20px', color: '#13c2c2' }} />,
      path: '/expenses'
    },
    { 
      title: 'Khách hàng',
      icon: <UserOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      path: '/customers'
    },
    { 
      title: 'Nhà cung cấp',
      icon: <ShopOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />,
      path: '/suppliers'
    },
    { 
      title: 'Quản lý thuốc',
      icon: <MedicineBoxOutlined style={{ fontSize: '20px', color: '#eb2f96' }} />,
      path: '/medicines'
    },
    ...(user?.role === 'admin' ? [{
      title: 'Quản lý người dùng',
      icon: <TeamOutlined style={{ fontSize: '20px', color: '#1890ff' }} />,
      path: '/users'
    }] : [])
  ];

  const khoMenuItems = [
    { 
      title: 'Xuất kho',
      icon: <ExportOutlined style={{ fontSize: '20px', color: '#1890ff' }} />,
      path: '/stock-out'
    },
    { 
      title: 'Nhập kho',
      icon: <ImportOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      path: '/stock-in'
    },
    { 
      title: 'Tồn kho',
      icon: <InboxOutlined style={{ fontSize: '20px', color: '#faad14' }} />,
      path: '/inventory'
    }
  ];

  // Danh sách báo cáo
  const reportMenuItems = [
    {
      title: 'Báo cáo kinh doanh',
      path: '/reports/revenue',
    },
    {
      title: 'Báo cáo nhập-xuất-tồn',
      path: '/reports/stock-in-out',
    },
    {
      title: 'Báo cáo lợi nhuận',
      path: '/reports/profit',
    },
    {
      title: 'Báo cáo công nợ',
      path: '/debts',
    },
  ];

  // User Avatar
  const getUserAvatar = () => (
    <div className={styles.logoContainer}>
      <Avatar
        size={32}
        src={user?.avatar}
        style={{
          backgroundColor: user?.avatar ? 'transparent' : '#1890ff',
          border: '2px solid #fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      >
        {!user?.avatar && (user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U')}
      </Avatar>
    </div>
  );

  return (
    <>
      <nav className={styles.bottomNavBar}>
        {/* Dashboard */}
        <button
          className={styles.navButton}
          onClick={() => navigate('/dashboard')}
        >
          <HomeOutlined className={`${styles.icon} ${activeTab === 'dashboard' ? styles.iconActive : ''}`} />
          <span className={`${styles.label} ${activeTab === 'dashboard' ? styles.labelActive : ''}`}>Dashboard</span>
        </button>

        {/* Kho */}
        <button
          ref={khoBtnRef}
          className={styles.navButton}
          onClick={() => setShowKhoPopover(prev => !prev)}
        >
          <InboxOutlined className={`${styles.icon} ${(activeTab === 'kho' || showKhoPopover) ? styles.iconActive : ''}`} />
          <span className={`${styles.label} ${(activeTab === 'kho' || showKhoPopover) ? styles.labelActive : ''}`}>Kho</span>
        </button>

        {/* Nút tạo mới ở giữa */}
        <div className={styles.createButtonContainer}>
          <button
            className={styles.createButton}
            onClick={() => setShowCreateModal(true)}
          >
            <PlusOutlined className={styles.createButtonIcon} />
          </button>
        </div>

        {/* Báo cáo */}
        <button
          className={styles.navButton}
          onClick={() => setShowReportDrawer(true)}
        >
          <BarChartOutlined className={`${styles.icon} ${(activeTab === 'report' || showReportDrawer) ? styles.iconActive : ''}`} />
          <span className={`${styles.label} ${(activeTab === 'report' || showReportDrawer) ? styles.labelActive : ''}`}>Báo cáo</span>
        </button>

        {/* Khác */}
        <button
          ref={moreMenuRef}
          className={styles.navButton}
          onClick={() => setShowMorePopover(prev => !prev)}
        >
          <MenuOutlined className={`${styles.icon} ${(activeTab === 'more' || showMorePopover) ? styles.iconActive : ''}`} />
          <span className={`${styles.label} ${(activeTab === 'more' || showMorePopover) ? styles.labelActive : ''}`}>Khác</span>
        </button>
      </nav>

      {/* Popover cho Kho */}
      {showKhoPopover && (
        <div
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowKhoPopover(false);
          }}
        >
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Quản lý kho</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowKhoPopover(false)}
              >
                <CloseOutlined className={styles.closeIcon} />
              </button>
            </div>
            <div className={styles.modalContent}>
              {khoMenuItems.map((item) => (
                <button
                  key={item.path}
                  className={`${styles.menuItem} ${location.pathname === item.path ? styles.menuItemActive : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setShowKhoPopover(false);
                  }}
                >
                  <div 
                    className={styles.menuItemIcon}
                    style={{ background: item.icon.props.style.color + '15' }}
                  >
                    {React.cloneElement(item.icon, {
                      style: { 
                        ...item.icon.props.style,
                        fontSize: '20px'
                      }
                    })}
                  </div>
                  <span className={styles.menuItemText}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Popover cho menu Khác */}
      {showMorePopover && (
        <div
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowMorePopover(false);
          }}
        >
          <div className={styles.modalContainer}>
            {/* User info + logout */}
            <div className={styles.userInfoSection}>
              {getUserAvatar()}
              <div className={styles.userInfo}>
                <div className={styles.userName}>{user?.full_name || user?.username}</div>
                <div className={styles.userRole}>{user?.username}</div>
              </div>
              <Button
                type="primary"
                danger
                size="small"
                className={styles.logoutButton}
                onClick={() => { logout(); navigate('/login'); }}
              >
                Đăng xuất
              </Button>
            </div>

            <div className={styles.modalContent}>
              {moreMenuItems.map((item) => (
                <button
                  key={item.path}
                  className={`${styles.menuItem} ${location.pathname === item.path ? styles.menuItemActive : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setShowMorePopover(false);
                  }}
                >
                  <div 
                    className={styles.menuItemIcon}
                    style={{ background: item.icon.props.style.color + '15' }}
                  >
                    {React.cloneElement(item.icon, {
                      style: { 
                        ...item.icon.props.style,
                        fontSize: '20px'
                      }
                    })}
                  </div>
                  <span className={styles.menuItemText}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal tạo mới */}
      {showCreateModal && (
        <div
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateModal(false);
          }}
        >
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Tạo mới</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowCreateModal(false)}
              >
                <CloseOutlined className={styles.closeIcon} />
              </button>
            </div>
            <div className={styles.modalContentCompact}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {createMenuItems.map((item) => (
                  <button
                    key={item.type}
                    className={styles.createMenuItem}
                    style={{
                      background: item.color,
                      border: `1px solid ${item.borderColor}`,
                      width: '100%',
                      minHeight: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px 8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleCreateNew(item.type)}
                  >
                    <span className={styles.createMenuItemIcon}>{item.icon}</span>
                    <span className={styles.menuItemText} style={{ fontSize: '12px', textAlign: 'center', lineHeight: '1.2' }}>{item.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Các Modal tạo mới */}
      <StockInModalMobile
        visible={showStockInModal}
        onCancel={() => setShowStockInModal(false)}
        medicines={medicines}
        suppliers={suppliers}
      />
      <StockOutModalMobile
        visible={showStockOutModal}
        onCancel={() => setShowStockOutModal(false)}
        medicines={medicines}
      />
      <CustomerModalMobile
        visible={showCustomerModal}
        onCancel={() => setShowCustomerModal(false)}
        onSubmit={handleCustomerSubmit}
        editingCustomer={null}
      />
      <SupplierModalMobile
        visible={showSupplierModal}
        onCancel={() => setShowSupplierModal(false)}
        onSave={handleSupplierSave}
      />
      <MedicineMobileModal
        visible={showMedicineModal}
        onCancel={() => setShowMedicineModal(false)}
        onSuccess={handleMedicineSuccess}
      />
      <ExpenseDetailModalMobile
        visible={showExpenseModal}
        onCancel={() => setShowExpenseModal(false)}
        onSuccess={handleExpenseSuccess}
        editingExpense={undefined}
        isLoadingDetail={false}
      />

      {/* Drawer danh sách báo cáo */}
      {showReportDrawer && (
        <div
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReportDrawer(false);
          }}
        >
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Danh sách báo cáo</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowReportDrawer(false)}
              >
                <CloseOutlined className={styles.closeIcon} />
              </button>
            </div>
            <div className={styles.modalContent}>
              {reportMenuItems.map((item) => (
                <button
                  key={item.path}
                  className={`${styles.menuItem} ${location.pathname === item.path ? styles.menuItemActive : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setShowReportDrawer(false);
                  }}
                >
                  <BarChartOutlined className={styles.reportMenuItemIcon} />
                  <span className={styles.menuItemText}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar; 